<div class="project-selector" role="group" aria-label="Projects">
  <button
    type="button"
    class="dk-btn dk-btn--outline dk-btn--secondary project-selector__all"
    [attr.aria-pressed]="isAll()"
    (click)="selectAll()"
  >
    @if (isAll()) {
      <lucide-icon name="check" [size]="14" aria-hidden="true" />
    }
    All Projects
    <span class="dk-badge dk-badge--count" aria-hidden="true">{{ projects().length }}</span>
    <span class="dk-visually-hidden">({{ projects().length }})</span>
  </button>

  @for (project of pinnedProjects(); track project.id) {
    <button
      type="button"
      class="dk-btn dk-btn--outline dk-btn--secondary project-selector__chip"
      [attr.aria-pressed]="isSelected(project)"
      [dkTooltip]="project.name"
      dkTooltipPosition="below"
      (click)="selectOnly(project)"
    >
      @if (isSelected(project)) {
        <lucide-icon name="check" [size]="14" aria-hidden="true" />
      }
      <app-project-avatar [project]="project" />
      <span class="project-selector__chip-name">{{ project.name }}</span>
      <app-pin-icon class="project-selector__chip-pin" />
    </button>
  }

  @if (pinnedProjects().length > 0) {
    <span class="dk-divider dk-divider--vertical project-selector__divider" aria-hidden="true"></span>
  }

  <button
    #trigger
    type="button"
    class="project-selector__box"
    cdkOverlayOrigin
    #origin="cdkOverlayOrigin"
    aria-haspopup="dialog"
    [attr.aria-label]="boxLabel()"
    [attr.aria-expanded]="open()"
    [attr.aria-controls]="open() ? panelId : null"
    [dkTooltip]="overflowTooltip()"
    dkTooltipPosition="below"
    (click)="toggleOpen()"
  >
    @for (project of boxShown(); track project.id) {
      <span class="project-selector__box-chip">
        <app-project-avatar [project]="project" />
        <span class="project-selector__box-name">{{ project.name }}</span>
      </span>
    } @empty {
      <span class="project-selector__placeholder">{{ placeholder() }}</span>
    }
    @if (boxOverflow().length > 0) {
      <span class="project-selector__box-chip project-selector__box-more">+{{ boxOverflow().length }}</span>
    }
    <span class="project-selector__chevron">
      <lucide-icon name="chevron-down" [size]="16" aria-hidden="true" />
    </span>
  </button>
</div>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="origin"
  [cdkConnectedOverlayOpen]="open()"
  [cdkConnectedOverlayPositions]="panelPositions"
  [cdkConnectedOverlayHasBackdrop]="true"
  cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
  cdkConnectedOverlayPanelClass="dk-overlay--popover"
  (backdropClick)="close()"
  (overlayKeydown)="onPanelKeydown($event)"
  (detach)="close()"
>
  <div
    class="dk-popover project-selector__panel"
    role="dialog"
    aria-label="Choose projects"
    [id]="panelId"
    cdkTrapFocus
    [cdkTrapFocusAutoCapture]="true"
  >
    <div class="dk-popover__header">
      <input
        #search
        type="search"
        class="dk-field dk-field--sm"
        placeholder="Search projects"
        aria-label="Search projects"
        autocomplete="off"
        cdkFocusInitial
        [value]="query()"
        (input)="onSearch($event)"
      />
    </div>

    <div class="dk-popover__body project-selector__body">
      @if (searching()) {
        @if (matches().length > 0) {
          <div role="group" aria-labelledby="project-selector-results">
            <div class="dk-listbox__group-label" id="project-selector-results" aria-live="polite">
              {{ matches().length }} {{ matches().length === 1 ? 'result' : 'results' }}
            </div>
            @for (hit of matches(); track hit.project.id) {
              <ng-container
                *ngTemplateOutlet="
                  row;
                  context: { $implicit: hit.project, before: hit.before, match: hit.match, after: hit.after }
                "
              />
            }
          </div>
        } @else {
          <div class="dk-empty-state dk-empty-state--compact" aria-live="polite">
            <p class="dk-empty-state__title">No projects match “{{ query().trim() }}”</p>
            <p class="dk-empty-state__description">Check the spelling, or clear the search.</p>
            <button type="button" class="dk-btn dk-btn--ghost dk-btn--sm" (click)="clearSearch(search)">
              Clear search
            </button>
          </div>
        }
      } @else {
        <div class="project-selector__row">
          <label class="dk-check project-selector__option">
            <input type="checkbox" class="dk-checkbox" [checked]="isAll()" (change)="onAllRowChange($event)" />
            <span class="project-selector__name">All projects</span>
            <span class="dk-badge dk-badge--count" aria-hidden="true">{{ projects().length }}</span>
          </label>
        </div>

        @if (pinnedProjects().length > 0) {
          <div role="group" aria-labelledby="project-selector-pinned" class="project-selector__group">
            <div class="dk-listbox__group-label" id="project-selector-pinned">
              Pinned · {{ pinnedProjects().length }} of {{ maxPinned }}
            </div>
            @for (project of pinnedProjects(); track project.id) {
              <ng-container *ngTemplateOutlet="row; context: { $implicit: project }" />
            }
          </div>
        }

        <div role="group" aria-labelledby="project-selector-all" class="project-selector__group">
          <div class="dk-listbox__group-label" id="project-selector-all">All projects</div>
          @for (project of unpinnedProjects(); track project.id) {
            <ng-container *ngTemplateOutlet="row; context: { $implicit: project }" />
          }
        </div>
      }
    </div>

    <p class="dk-caption project-selector__status" aria-live="polite">
      @if (pinLimitHit()) {
        Pin limit reached. Unpin one first.
      } @else {
        Pin projects for quick access · {{ pinnedProjects().length }} of {{ maxPinned }}
      }
    </p>

    <div class="dk-popover__footer">
      <button type="button" class="dk-popover__action" (click)="openCreateProject()">Create New Project</button>
      <button
        type="button"
        class="dk-popover__action project-selector__clear"
        [disabled]="isAll()"
        (click)="selectAll()"
      >
        Clear selection
      </button>
    </div>
  </div>
</ng-template>

<ng-template #row let-project let-before="before" let-match="match" let-after="after">
  <div class="project-selector__row">
    <label class="dk-check project-selector__option">
      <input type="checkbox" class="dk-checkbox" [checked]="isSelected(project)" (change)="toggleSelection(project)" />
      <app-project-avatar [project]="project" />
      <span class="project-selector__name">
        @if (match) {
          {{ before }}<mark>{{ match }}</mark
          >{{ after }}
        } @else {
          {{ project.name }}
        }
      </span>
    </label>
    <button
      type="button"
      class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--icon dk-btn--sm project-selector__pin"
      [attr.aria-pressed]="isPinned(project)"
      [attr.aria-label]="(isPinned(project) ? 'Unpin ' : 'Pin ') + project.name"
      [dkTooltip]="isPinned(project) ? 'Unpin' : 'Pin to bar'"
      dkTooltipPosition="before"
      (click)="togglePin(project)"
    >
      <app-pin-icon />
    </button>
  </div>
</ng-template>
